import { organizationFields, contactFields, registerContactFields } from "../config/FieldConfigurations";
import { ORGANIZATION_TYPES, ORGANIZATION_TYPES_SHORT, MODEL_TYPES } from "@/utils/constants";

/**
 * Composable for managing field access state.
 */
export function useFieldAccessState() {

  /**
   * Retrieves the field configuration for a given model type and field.
   * @param {string} modelType - The type of the model (e.g., Organization, Contact).
   * @param {Object} field - The field object to retrieve configuration for.
   * @returns {Object|null} The field configuration or null if not found.
   */
  function getFieldConfig(modelType, field) {
    if (modelType === MODEL_TYPES.Organization) {
      return organizationFields.find(f => f.field === field.field);
    } else if (modelType === MODEL_TYPES.Contact) {
      return contactFields.find(f => f.field === field.field);
    } else if (modelType === MODEL_TYPES.RegisterContact) {
      return registerContactFields.find(f => f.field === field.field);
    }
    return null;
  }

  /**
   * Determines if a field is visible based on model type, field, and organization type.
   * @param {string} modelType - The type of the model.
   * @param {Object} field - The field object.
   * @param {string} organizationType - The type of the organization.
   * @returns {boolean} True if the field is visible, otherwise false.
   */
  function determineVisible(modelType, field, organizationType) {
    if (!organizationType) {
      return false;
    }
    const fieldConfig = getFieldConfig(modelType, field);
    if (!fieldConfig) return false;
    const isVisible = fieldConfig.visible.includes(ORGANIZATION_TYPES_SHORT.ALL) || fieldConfig.visible.includes(organizationType);
    return isVisible;
  }

  /**
   * Determines if a field is required based on model type, field, and organization type.
   * @param {string} modelType - The type of the model.
   * @param {Object} field - The field object.
   * @param {string} organizationType - The type of the organization.
   * @returns {boolean} True if the field is required, otherwise false.
   */
  function determineRequired(modelType, field, organizationType) {
    if (!organizationType) {
      return false;
    }
    const fieldConfig = getFieldConfig(modelType, field);
    if (!fieldConfig) return false;
    const isRequired = fieldConfig.required.includes(ORGANIZATION_TYPES_SHORT.ALL) || fieldConfig.required.includes(organizationType);
    return isRequired;
  }

  /**
   * Reduces a list of fields into an object with their access properties.
   * @param {Array} fields - The list of fields.
   * @returns {Object} An object mapping field names to their access properties.
   */
  function reduceFieldAccess(fields) {
    return fields.reduce((acc, field) => {
      acc[field.field] = {
        visible: field.visible,
        required: field.required,
      };
      return acc;
    }, {});
  }

  /**
   * Converts a long organization type to its short equivalent.
   * @param {string} organizationType - The long organization type.
   * @returns {string|null} The short organization type or null if not found.
   */
  function convertToShortType(organizationType) {
    const isShortType = Object.values(ORGANIZATION_TYPES_SHORT).includes(organizationType);
    if (isShortType) {
      return organizationType;
    }
    const organizationTypeLong = Object.keys(ORGANIZATION_TYPES).find(key => ORGANIZATION_TYPES[key] === organizationType);
    return organizationTypeLong ?? null;
  }

  /**
   * Maps fields with their visibility and required access properties.
   * @param {Array} fields - The list of fields.
   * @param {string} modelType - The type of the model.
   * @param {string} organizationType - The type of the organization.
   * @returns {Array} The mapped fields with access properties.
   */
  function mapFieldsWithAccess(fields, modelType, organizationType) {
    return fields.map(field => ({
      field: field.field,
      visible: determineVisible(modelType, field, organizationType),
      required: determineRequired(modelType, field, organizationType),
    }));
  }

  /**
   * Retrieves access properties for all fields of a given model type and organization type.
   * @param {string} modelType - The type of the model.
   * @param {string} organizationType - The type of the organization.
   * @returns {Object} An object mapping field names to their access properties.
   */
  function getAllFieldAccess(modelType, organizationType) {
    organizationType = convertToShortType(organizationType);
    if (modelType === MODEL_TYPES.Organization) {
      return reduceFieldAccess(getAllOrganizationFieldAccess(modelType, organizationType));
    } else if (modelType === MODEL_TYPES.Contact) {
      return reduceFieldAccess(getAllContactFieldAccess(modelType, organizationType));
    } else if (modelType === MODEL_TYPES.RegisterContact) {
      return reduceFieldAccess(getAllContactFieldAccess(modelType, organizationType));
    }
    return {};
  }

  /**
   * Retrieves access properties for a specific field of a given model type and organization type.
   * @param {string} modelType - The type of the model.
   * @param {string} fieldName - The name of the field.
   * @param {string} organizationType - The type of the organization.
   * @returns {Object} The access properties of the field.
   */
  function getFieldAccess(modelType, fieldName, organizationType) {
    organizationType = convertToShortType(organizationType);
    if (modelType === MODEL_TYPES.Organization) {
      return getOrganizationFieldAccess(modelType, fieldName, organizationType);
    } else if (modelType === MODEL_TYPES.Contact) {
      return getContactFieldAccess(modelType, fieldName, organizationType);
    } else if (modelType === MODEL_TYPES.RegisterContact) {
      return getContactFieldAccess(modelType, fieldName, organizationType);
    }
    return { visible: false, required: false };
  }

  /**
   * Retrieves access properties for all organization fields.
   * @param {string} modelType - The type of the model.
   * @param {string} organizationType - The type of the organization.
   * @returns {Array} The list of organization fields with access properties.
   */
  function getAllOrganizationFieldAccess(modelType, organizationType) {
    organizationType = convertToShortType(organizationType);
    if (modelType === MODEL_TYPES.Organization) {
      return mapFieldsWithAccess(organizationFields, modelType, organizationType);
    }
    return []; // Default return for invalid modelType
  }

  /**
   * Retrieves access properties for all contact fields.
   * @param {string} modelType - The type of the model.
   * @param {string} organizationType - The type of the organization.
   * @returns {Array} The list of contact fields with access properties.
   */
  function getAllContactFieldAccess(modelType, organizationType) {
    organizationType = convertToShortType(organizationType);
    if (modelType === MODEL_TYPES.RegisterContact) {
      return mapFieldsWithAccess(registerContactFields, modelType, organizationType);
    } else if (modelType === MODEL_TYPES.Contact) {
      return mapFieldsWithAccess(contactFields, modelType, organizationType);
    }
    return []; // Default return for invalid modelType
  }

  /**
   * Determines if the physical address field should be shown for a given organization type.
   * @param {string} organizationType - The type of the organization.
   * @returns {boolean} True if the physical address field should be shown, otherwise false.
   */
  function showPhysicalAddress(organizationType) {
    return organizationFields.some(field => field.field === 'address1' && field.visible.includes(organizationType));
  }

  /**
   * Determines if the mailing address field should be shown for a given organization type.
   * @param {string} organizationType - The type of the organization.
   * @returns {boolean} True if the mailing address field should be shown, otherwise false.
   */
  function showMailingAddress(organizationType) {
    return organizationFields.some(field => field.field === 'mailAddress1' && field.visible.includes(organizationType));
  }

  /**
   * Retrieves access properties for a specific organization field.
   * @param {string} modelType - The type of the model.
   * @param {string} fieldName - The name of the field.
   * @param {string} organizationType - The type of the organization.
   * @returns {Object} The access properties of the organization field.
   */
  function getOrganizationFieldAccess(modelType, fieldName, organizationType) {
    const field = organizationFields.find(f => f.field === fieldName);
    if (!field) return { visible: false, required: false };
    return {
      visible: determineVisible(modelType, field, organizationType),
      required: determineRequired(modelType, field, organizationType),
    };
  }

  /**
   * Retrieves access properties for a specific contact field.
   * @param {string} modelType - The type of the model.
   * @param {string} fieldName - The name of the field.
   * @param {string} organizationType - The type of the organization.
   * @returns {Object} The access properties of the contact field.
   */
  function getContactFieldAccess(modelType, fieldName, organizationType) {
    const fields = modelType === MODEL_TYPES.RegisterContact ? registerContactFields : contactFields;
    const field = fields.find(f => f.field === fieldName);
    if (!field) return { visible: false, required: false }; // Default return for missing field
    return {
      visible: determineVisible(modelType, field, organizationType),
      required: determineRequired(modelType, field, organizationType),
    };
  }

  function getFieldValidationMessage(modelType, fieldName) {
    const fieldConfig = getFieldConfig(modelType, { field: fieldName });
    return fieldConfig ? fieldConfig.validationMessage : '';
  }

  return {
    getAllFieldAccess,
    getFieldAccess,
    showPhysicalAddress,
    showMailingAddress,
    getFieldValidationMessage,
  };
}